import React, {ReactElement, useContext} from "react";
import Countdown from "./Countdown";
import {HostBubblePlaceHolder} from "./HostBubble";
import HostName from "./HostName";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../App";
import {triviaStateDuration, TriviaStatus} from "../../state/state";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import {AccessoryLgWidth} from "../accessory/accessoryConstants";
import Chat from "../chat/Chat";
import {useWindowSize} from "engage-client/hooks/useResizeObserver";

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	getReady: {
		display: "flex",
		flex: 1,
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		textAlign: "center",
		height:"100%",
		position: "relative"
	},
	message: {
		margin: [75,0,50,0]
	},
	top: {
		[theme.breakpoints.up("md")]: {
			marginTop: 125,
		}
	},
	bottom: {
		display: "flex",
		alignItems: "center",
		flexDirection: "column",
		[theme.breakpoints.up("md")]: {
			flexDirection: "row",
		},
		marginTop: 50,
		[theme.breakpoints.up("xxl")]: {
			marginTop: "5vh",
		}
	},
	timerText: {
		[theme.breakpoints.up("md")]: {
			margin: [0,0,0,12],
		},
		[theme.breakpoints.down("md")]: {
			margin: [12,0,0]
		}
	},
	chatContainer: {
		position: "absolute",
		bottom: 0,
		right: 0,
		[theme.breakpoints.down("md")]: {
			left: 0
		},
		[theme.breakpoints.up("lg")]: {
			width: AccessoryLgWidth
		},
	}
}));

type GetReadyProps = {
	streamUrl?: string
	selfTimed?: boolean
	sharedDisplay: boolean
}

export default function GetReady(props: GetReadyProps) {
	const theme:Theme = useTheme();

	const {appState} = useContext(EngageAppContext.current);
	const classes = useStyles();
	const stateDuration = triviaStateDuration(appState.common);

	const rectangleVideo = appState.common.gameOptions.introVideoRectangle && appState.common.state === TriviaStatus.Starting;

	const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const extraSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
	const extraLargeScreen = useMediaQuery(theme.breakpoints.up("xxl"));
	let extraLargeCountdown = -1;
	
	const windowHeight = useWindowSize().height;
	if (windowHeight) {
		extraLargeCountdown = (25 * windowHeight) / 100;
	}

	const width = rectangleVideo ? extraSmallScreen ? 300 : smallScreen ? 500 : 700 : smallScreen ? 250 : 325;

	const streamUrl = props.streamUrl ?? appState.common.gameOptions.streamUrl;
	const selfTimed = props.selfTimed ?? appState.common.gameOptions.selfTimed;

	const showStream = (streamUrl?.length ?? 0) > 1;

	let topContent: ReactElement;
	let bottomContent: ReactElement|undefined;

	if(showStream) {
		topContent = <div className={classes.top}>
			{!rectangleVideo && <HostName size={smallScreen ? 325 : 465} name={appState.common.gameOptions.hostName || "Host"}/>}
			<HostBubblePlaceHolder embedURL={streamUrl!} width={width}/>
		</div>;


		bottomContent = <div className={classes.bottom}>
			<Countdown start={(appState.common ?? {timeMark: 1}).timeMark}
						   duration={stateDuration}
						   pause={(appState.common ?? {pauseMark: 1}).pauseMark}
						   size={80}
						   thickness={5} 
						   color={theme.welcome.gameCountdownColor ?? "white"} />
			<DisplayText className={classes.timerText} variant={TextVariant.s34reg} color={"#FFF"}>
				Get ready! The game is about to begin...
			</DisplayText>
		</div>;
	} else {
		if(selfTimed) {
			topContent = <DisplayText className={classes.message} variant={TextVariant.s34reg} color="white">
				Get ready! The game is about to begin...
			</DisplayText>;

		} else {
			topContent = <Countdown start={(appState.common ?? {timeMark: 1}).timeMark}
										duration={stateDuration}
										pause={(appState.common ?? {pauseMark: 1}).pauseMark}
										size={smallScreen ? 130 : extraLargeScreen ? extraLargeCountdown : 250}
										thickness={extraLargeScreen ? 15 : 10}
										color={theme.welcome.gameCountdownColor ?? "white"} />;

			bottomContent = <DisplayText className={classes.bottom} variant={TextVariant.subtitle} color={"#FFF"}>
				The game will begin soon!
			</DisplayText>;
		}
	}

	return (
		<div className={classes.getReady}>
			<div />
			{topContent}
			{bottomContent}
			{appState.common.gameOptions.showChat && !props.sharedDisplay &&
			<Chat popup={true} className={classes.chatContainer}/>
			}
		</div>
	);
};
